/* Internal usage tracker — mirrors PostHog-style capture into OUR database
   so dispute evidence and admin analytics never depend on a 3rd party.
   Logged-in users: every event. Anonymous visitors: page views + a small
   whitelist of funnel events, keyed by a random first-party id (no cookies). */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_MS = 30 * 60 * 1000;
const ANON_EVENTS = new Set(['pageview', 'page_view', 'view_pricing', 'cta_click', 'exit_intent_shown', 'exit_intent_claim',
  'upgrade_modal_shown', 'signup_intent', 'crs_banner_click', 'sign_up']);
let queue = [];
let started = false;

function sessionKey() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem('cmx_usage_session');
    let s = raw ? JSON.parse(raw) : null;
    if (!s || now - (s.last || 0) > SESSION_MS) {
      s = { key: `s_${now.toString(36)}_${Math.random().toString(36).slice(2, 8)}`, last: now };
    }
    s.last = now;
    sessionStorage.setItem('cmx_usage_session', JSON.stringify(s));
    return s.key;
  } catch (_) {
    return 'no-session';
  }
}

export function anonId() {
  try {
    let id = localStorage.getItem('cmx_anon_id');
    if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
      const rnd = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID().replace(/-/g, '') : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      id = `a_${rnd}`.slice(0, 64);
      localStorage.setItem('cmx_anon_id', id);
    }
    return id;
  } catch (_) {
    return '';
  }
}

export function logUsage(event, props = {}) {
  try {
    if (!event) return;
    const name = String(event).slice(0, 40);
    if (!localStorage.getItem('token') && !ANON_EVENTS.has(name)) return;
    queue.push({ event: name, path: window.location.pathname, ts: new Date().toISOString(), props });
    if (queue.length >= 40) flush();
  } catch (_) { /* never break the app for analytics */ }
}

function flush(keepalive = false) {
  if (!queue.length) return;
  const token = localStorage.getItem('token');
  const anon = anonId();
  if (!token && !anon) { queue = []; return; }
  const events = queue.splice(0, 100);
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API_URL}/api/track`, {
      method: 'POST',
      keepalive,
      headers,
      body: JSON.stringify({ session_key: sessionKey(), anon_id: anon, events }),
    }).catch(() => {});
  } catch (_) { /* ignore */ }
}

export function trackPageview(path) {
  logUsage('pageview', { path });
}

export function initUsageTracker() {
  if (started || typeof document === 'undefined') return;
  started = true;
  let lastClick = 0;
  document.addEventListener('click', (e) => {
    try {
      const now = Date.now();
      if (now - lastClick < 250) return;
      const el = e.target.closest && e.target.closest('button, a, [role="button"], [data-testid], select');
      if (!el) return;
      const label = el.getAttribute('data-testid') || (el.innerText || el.value || '').trim().slice(0, 60);
      if (!label) return;
      lastClick = now;
      logUsage('click', { label });
    } catch (_) { /* ignore */ }
  }, { capture: true, passive: true });
  setInterval(() => flush(false), 15000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}
